/**
 * Host notification utilities for communicating with parent application (e.g., Revit, BIM software)
 */

import { NotifyHostRequest, ComplianceIssue } from 'types/types';

/**
 * Extract compliance issues from rendered articles in the DOM
 * Maps to Host-expected status values: Compliant, Undetermined, Invalid, NotValidated
 */
function extractComplianceIssues(contentElement: HTMLDivElement): ComplianceIssue[] {
  const complianceIssues: ComplianceIssue[] = [];
  
  // Find all article elements
  const articles = contentElement.querySelectorAll('.article');
  
  articles.forEach(article => {
    // Get the conclusion element
    const conclusion = article.querySelector('.conclusion');
    if (!conclusion) return;
    
    // Skip "Not Applicable" articles - Host doesn't need them
    if (conclusion.classList.contains('not-applicable')) return;
    
    // Determine if this is a compliance issue (not compliant)
    const isCompliant = conclusion.classList.contains('valid');
    if (isCompliant) return; // Skip compliant articles
    
    // Map severity to Host-expected values
    let severity = 'NotValidated'; // Default
    if (conclusion.classList.contains('invalid')) severity = 'Invalid';
    else if (conclusion.classList.contains('undetermined')) severity = 'Undetermined';
    
    // Get the article title from h2
    const titleElement = article.querySelector('h2');
    const codeReference = titleElement?.textContent?.trim() || 'No title';
    
    // Get the explanation text from the conclusion
    const description = conclusion.textContent?.trim() || 'No description';
    
    complianceIssues.push({
      codeReference,
      description,
      recommendedAction: "", // Not included in current response format
      severity
    });
  });
  
  return complianceIssues;
}

/**
 * Extract overall conclusion from rendered content
 * Maps to Host-expected status values: Compliant, Undetermined, Invalid
 * Always determines a status - never returns NotValidated
 * 
 * Expected structure from API:
 * <div class="overall-conclusion undetermined|valid|invalid|not-applicable">
 *   <h1>Conclusion: Undetermined</h1>
 *   <p>The element is undetermined...</p>
 * </div>
 */
function extractOverallConclusion(contentElement: HTMLDivElement): {
  text: string;
  type: 'Compliant' | 'Invalid' | 'Undetermined';
} {
  // Look for the overall-conclusion div directly
  const overallConclusionDiv = contentElement.querySelector('.overall-conclusion');
  
  if (overallConclusionDiv) {
    const text = overallConclusionDiv.textContent?.trim() || '';
    
    // Map type from classes on the overall-conclusion div
    if (overallConclusionDiv.classList.contains('valid') || overallConclusionDiv.classList.contains('compliant')) {
      return { text, type: 'Compliant' };
    } else if (overallConclusionDiv.classList.contains('invalid') || overallConclusionDiv.classList.contains('not-compliant')) {
      return { text, type: 'Invalid' };
    } else if (overallConclusionDiv.classList.contains('undetermined')) {
      return { text, type: 'Undetermined' };
    } else if (overallConclusionDiv.classList.contains('not-applicable')) {
      // Not applicable maps to Undetermined for host communication
      return { text, type: 'Undetermined' };
    }
    
    // Fallback: parse text content if no class match
    const lowerText = text.toLowerCase();
    if (lowerText.includes('valid') && !lowerText.includes('invalid')) {
      return { text, type: 'Compliant' };
    } else if (lowerText.includes('invalid') || lowerText.includes('not compliant') || 
               lowerText.includes('non-compliant') || lowerText.includes('does not comply')) {
      return { text, type: 'Invalid' };
    } else if (lowerText.includes('undetermined') || lowerText.includes('cannot determine') ||
               lowerText.includes('insufficient') || lowerText.includes('not applicable')) {
      return { text, type: 'Undetermined' };
    }
    
    // If we still can't determine, default to Undetermined
    console.warn('[extractOverallConclusion] Could not determine type from classes or text, defaulting to Undetermined:', text);
    return { text, type: 'Undetermined' };
  }
  
  // If no overall conclusion found at all, default to Undetermined
  console.warn('[extractOverallConclusion] No overall conclusion section found, defaulting to Undetermined');
  return { text: 'No overall conclusion found', type: 'Undetermined' };
}

/**
 * Send compliance analysis to host application
 */
export function notifyHost(
  elementId: string,
  contentElement: HTMLDivElement | null
): void {
  if (!contentElement) {
    console.warn('Cannot notify host: content element is null');
    return;
  }

  try {
    // Extract data from the rendered DOM
    const overallConclusion = extractOverallConclusion(contentElement);
    const complianceIssues = extractComplianceIssues(contentElement);

    const request: NotifyHostRequest = {
      elementId,
      status: overallConclusion.type,
      complianceIssues
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const result = {
      "Type": "NotifyHost",
      "Data": JSON.stringify(request)
    };

    // Send message to host (e.g., C# WebView in Revit plugin)
    // eslint-disable-next-line
    eval('window.chrome?.webview?.postMessage(result);');
    
    console.log('[notifyHost] Sent to host:', { elementId, status: overallConclusion.type, issuesCount: complianceIssues.length });
  } catch (ex) {
    console.error('Failed to notify host:', ex);
  }
}

/**
 * Request host version from the parent application
 */
export function requestHostVersion(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const result = {
      "Type": "RequestHostVersion",
      "Data": ""
    };

    // Send message to host (e.g., C# WebView in Revit plugin)
    // eslint-disable-next-line
    eval('window.chrome?.webview?.postMessage(result);');
    
    console.log('[requestHostVersion] Requested host version from host');
  } catch (ex) {
    console.error('Failed to request host version:', ex);
  }
}

/**
 * Request tools list from the parent application
 */
export function requestToolsList(): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const result = {
      "Type": "RequestToolsList",
      "Data": ""
    };

    // Send message to host (e.g., C# WebView in Revit plugin)
    // eslint-disable-next-line
    eval('window.chrome?.webview?.postMessage(result);');
    
    console.log('[requestToolsList] Requested tools list from host');
  } catch (ex) {
    console.error('Failed to request tools list:', ex);
  }
}
